import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, X } from 'lucide-react';
import { inTime, openSave, putShot, shootElement, takeSaveLabel } from './sandboxSaves';
import { useLanguage } from '../../contexts/LanguageContext';

// THE WALK: every phase of the reader, photographed in the order the phase bar lists them.
//
// It runs ON THE VIEWER'S PAGE and not on the Saves tab, because the pixels are here. The
// viewer keeps every phase mounted and shows one — so a picture of "all the phases" is not
// one screenshot of a long page, it is eight screenshots of the same box with a different
// phase in it, and the only thing that can put a phase in that box is the viewer's own tab
// state. This component is handed the way to move it (`onGo`) and the list to move through.
//
// WHY IT IS VISIBLE. The alternative is mounting a second reader off-screen and walking
// that, which costs a second PDF, a second set of WebGL contexts and a second copy of every
// store — and hides the one thing worth seeing, which is whether the page it photographed
// was finished drawing. Here, somebody watches their reader flick through its phases and
// can see what went into the save.
//
// WHAT IT CAPTURES is the element marked `data-capture-root` — the reader's whole column,
// phase bar included. The hidden phases are `display: none` and draw nothing, so what
// lands is the phase that is open, at its full height rather than the window's.

// HOW LONG A PHASE IS GIVEN TO FINISH DRAWING before its picture is taken.
//
// A phase becomes visible and then does work: the PDF renders a page to a canvas, a mesh
// list fetches its thumbnails, a table lays out fourteen steps. None of that announces
// itself — there is no "this phase is ready" in this app and inventing one would mean a
// promise threaded through every phase in the pipeline for the benefit of a screenshot.
//
// So it is a wait. Two frames for React to commit and the browser to lay out, then a beat
// of grace, then any image still loading is waited for by name — and a picture of a
// half-drawn phase is a save that lies about the version, so the beat is real.
const SETTLE_MS = 700;

// AND HOW LONG ANY ONE STAGE MAY TAKE BEFORE IT IS CALLED STUCK. Generous enough for a tall
// phase on a slow machine, short enough that a walk which is not going to finish says so
// rather than spinning under a pill that reads the same all afternoon.
const DRAW_MS = 90_000;
const SEND_MS = 60_000;
// The pictures a phase is still fetching. Waited for, but not forever: an image that never
// loads is part of what the page looks like, and a save is not going to be held up by it.
const IMAGES_MS = 5_000;

const frames = () => new Promise<void>((done) => {
  requestAnimationFrame(() => requestAnimationFrame(() => done()));
});

const wait = (ms: number) => new Promise<void>((done) => { window.setTimeout(done, ms); });

// WHAT THE WALK IS DOING, where something other than a person can read it. Tagged so the
// headless browser can pick these out of everything else a page says.
const say = (line: string) => console.log(`[saves] ${line}`);

async function settle(root: HTMLElement) {
  await frames();
  await wait(SETTLE_MS);
  // The pictures a phase went and fetched when it became visible. `decode` resolves when
  // the bytes are drawable, which is the thing that matters here; a failed one resolves
  // too, because a broken image is part of what the page looks like — and an image that
  // never answers at all is given up on rather than waited for.
  await Promise.race([
    Promise.all([...root.querySelectorAll('img')]
      .filter((img) => !img.complete)
      .map((img) => img.decode().catch(() => undefined))),
    wait(IMAGES_MS),
  ]);
  await frames();
}

export function SandboxSaveWalk({
  docId,
  docName,
  tabs,
  startTab,
  onGo,
  onDone,
}: {
  docId: string;
  docName: string;
  // The phase bar's own items, in its own order: the ids to walk and the labels to file
  // each picture under.
  tabs: { id: string; label: string }[];
  // Where the reader was standing before the walk, to be put back afterwards. A save is
  // something you ask for from another tab; coming back to the manual to find it on Phase 7
  // because that is where the camera stopped would be the save rearranging the workshop.
  startTab: string;
  onGo: (tab: string) => void;
  // Called once, however it ended. The page it navigates to is the caller's business.
  onDone: (error: string) => void;
}) {
  const { t } = useLanguage();
  const [at, setAt] = useState(0);
  const [phase, setPhase] = useState('');
  // HOW LONG THIS STAGE HAS BEEN GOING. The first walk sat on one pill for minutes with
  // nothing to say whether it was working or stuck, and the answer turned out to be in the
  // console. A clock on the pill is the difference between "it is slow" and "it is gone".
  const [since, setSince] = useState(() => Date.now());
  const [secs, setSecs] = useState(0);
  // THE WALK THAT IS CURRENTLY THE WALK — one object per run of the effect, and the only
  // thing that says whether it may still touch the page.
  //
  // WHAT THIS REPLACED, AND WHY IT COULD NOT WORK. It was a `started` ref that let the walk
  // run once, and an `alive` ref that the cleanup set to false. In development an effect
  // runs mount -> CLEANUP -> mount (StrictMode), and the sandbox is the one build that runs
  // in development — so the cleanup killed the run that the guard had just let start, and
  // the second mount declined to start another. The loop's `alive` test then skipped every
  // phase and the finish was guarded by the same flag, so nothing was photographed, nothing
  // failed, and the pill sat on "screen 1 of 8" for as long as anybody watched it.
  //
  // A token per run is the shape that survives both: the first run is cancelled by its own
  // cleanup, the second is the one that happens, and a real unmount cancels whichever is
  // going. Nothing outlives the component.
  const run = useRef<{ alive: boolean } | null>(null);

  useEffect(() => {
    setSecs(0);
    const tick = window.setInterval(
      () => setSecs(Math.round((Date.now() - since) / 1000)), 1000,
    );
    return () => window.clearInterval(tick);
  }, [since]);

  useEffect(() => {
    const mine = { alive: true };
    run.current = mine;

    const walk = async (going: { alive: boolean }) => {
      const label = takeSaveLabel();
      let failed = '';
      const top = window.scrollY;
      try {
        const root = document.querySelector<HTMLElement>('[data-capture-root]');
        if (!root) throw new Error('nothing to photograph');
        say(`opening a save of ${tabs.length} screens`);
        const saveId = await inTime('opening the save', SEND_MS, openSave({
          label, docId, docName, expected: tabs.length,
        }));
        for (let i = 0; i < tabs.length && going.alive; i++) {
          setAt(i);
          setPhase(tabs[i].label);
          setSince(Date.now());
          const began = Date.now();
          onGo(tabs[i].id);
          // THE TOP OF THE PAGE, BEFORE EVERY SHOT. The phase bar is sticky: scrolled down,
          // it sits translated into the middle of the column, and the picture would have
          // the bar floating over the phase with a gap where it belongs.
          window.scrollTo(0, 0);
          await settle(root);
          if (!going.alive) break;
          // NAMED IN THE FAILURE, both of them: "Phase 4 took longer than 90s" says which
          // screen and which half of taking it, where "the save failed" says neither.
          const png = await inTime(`drawing ${tabs[i].label}`, DRAW_MS, shootElement(root));
          await inTime(`sending ${tabs[i].label}`, SEND_MS, putShot(saveId, i, png, {
            phase: tabs[i].id,
            label: tabs[i].label,
            w: root.offsetWidth,
            h: root.offsetHeight,
          }));
          // OUT LOUD, one line per screen. The pill says this to whoever is watching the
          // page; nobody is watching the page when a promotion takes the save, and the
          // terminal it runs from was printing "walking the phases…" and then nothing for
          // minutes. The headless browser forwards these (see scripts/capture/save.mjs), so
          // the same sentence reaches whoever is actually there — with how long the screen
          // took, which is the number that says whether it is slow or gone.
          //
          // console.log in a shipped page, deliberately: this build is the sandbox, the walk
          // is a thing somebody asked for, and it says only what the pill already says.
          say(`${i + 1}/${tabs.length} ${tabs[i].label} — ${Math.round((Date.now() - began) / 1000)}s`);
        }
        // BACK WHERE IT WAS. The phase the reader opens on is remembered per manual and
        // written from `activeTab` (see NoticeViewer), so a walk that ended on the last
        // phase would have moved somebody's bookmark as a side effect of photographing it.
        onGo(startTab);
        window.scrollTo(0, top);
      } catch (e) {
        // WHAT WAS TAKEN IS KEPT. The save row exists from the first request and the
        // screens land one at a time, so a walk that fails on Phase 5 leaves five real
        // pictures and a count saying five of eight — which is a save somebody can look at
        // and a fact they can act on, where deleting the lot would be neither.
        failed = e instanceof Error ? e.message : String(e);
      }
      say(failed ? `stopped: ${failed}` : 'done');
      if (going.alive) onDone(failed);
    };

    // AFTER A TICK, so a run cancelled the instant it starts costs nothing. StrictMode's
    // cleanup lands before this fires, which is what keeps the discarded first run from
    // opening a save row nobody asked for and leaving it in the list at nought of eight.
    const start = window.setTimeout(() => { if (mine.alive) void walk(mine); }, 50);

    return () => {
      mine.alive = false;
      window.clearTimeout(start);
    };
    // Once, on mount. Every input is fixed for the life of the walk, and re-running it
    // because a label changed would take a second save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    // NOT IN THE PICTURE. What is photographed is the whole app shell now, and this sits
    // inside it — `fixed` places it in the viewport but it is still a node in that subtree,
    // and a rasteriser clones the tree rather than the paint. The mark the hidden phases
    // carry is the same one, read by the same filter (see sandboxSaves).
    <div
      data-capture-skip=""
      className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-3
      rounded-full border border-slate-300 bg-white/95 px-5 py-3 text-sm font-medium
      text-slate-700 shadow-lg backdrop-blur"
    >
      <Camera className="h-4 w-4" />
      <span>
        {t('notice.saves.walking')
          .replace('{n}', String(at + 1))
          .replace('{m}', String(tabs.length))
          .replace('{phase}', phase)}
      </span>
      <span className="tabular-nums text-slate-400">{secs}s</span>
      <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
      {/* A WAY OUT THAT IS NOT THE RELOAD BUTTON. What has been taken is already on the
          server, so stopping keeps the screens that landed and leaves the save short —
          which the list says out loud (six of eight). */}
      <button
        type="button"
        title={t('notice.saves.stop')}
        onClick={() => {
          if (run.current) run.current.alive = false;
          onDone('');
        }}
        className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-200
          hover:text-slate-700"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
